import React from 'react';
import moment from 'moment-timezone';

import {
	Box,
	Button,
	FormHelperText,
	Grid,
	IconButton,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { RiCloseLine } from 'react-icons/ri';
import TimePicker from 'components/TimePicker';

import { DeliveryTime, OpeningHoursDayType } from 'common/types';
import { dayTypeOptions } from 'views/Store/Tabs/OpeningHours/utils';
import { useTranslation } from 'services/localization/useTranslation';
import { getDefaultDeliveryTimeSlot, isDeliveryTimeSlotDuplicate } from '../utils';
import { getDayTypeTranslation } from 'common/utils/translations';

interface Props {
	value: DeliveryTime;
	addedDayTypes: OpeningHoursDayType[];
	onChange: (value: DeliveryTime) => void;
	onDelete: (slotIndex: number) => void;
}

const SingleDeliveryTime = (props: Props) => {
	const { value, addedDayTypes, onChange, onDelete } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const handleDayTypeChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const newDayType = e.target.value as OpeningHoursDayType;
		onChange({ ...value, dayType: newDayType });
	};

	const handleStartTimeChange = (startTime: string, index: number) => {
		const newSlots = value.slots.map((slot, i) => {
			if (i !== index) return slot;
			const startTimeMoment = moment(startTime, 'HH:mm');
			const endTimeMoment = moment(slot.endTime, 'HH:mm');
			const endTime = startTimeMoment.isSameOrAfter(endTimeMoment)
				? startTimeMoment.add(startTime === '23:45' ? 14 : 15, 'minutes').format('HH:mm')
				: slot.endTime;
			return { startTime, endTime };
		});
		onChange({ ...value, slots: newSlots });
	};

	const handleEndTimeChange = (endTime: string, index: number) => {
		const newSlots = value.slots.map((slot, i) => (i === index ? { ...slot, endTime } : slot));
		onChange({ ...value, slots: newSlots });
	};

	const handleAddNewSlot = () => {
		const newSlots = [...value.slots, getDefaultDeliveryTimeSlot(value.slots)];
		onChange({ ...value, slots: newSlots });
	};

	const renderDayTypeItems = () =>
		dayTypeOptions
			.filter(
				(dayTypeItem) => !addedDayTypes.includes(dayTypeItem) || dayTypeItem === value.dayType,
			)
			.map((dayTypeItem) => (
				<MenuItem key={dayTypeItem} value={dayTypeItem}>
					{getDayTypeTranslation(dayTypeItem, t)}
				</MenuItem>
			));

	return (
		<Grid container spacing={3} alignItems="flex-start">
			<Grid item xs={10} md={3}>
				<TextField
					fullWidth
					select
					color="primary"
					variant="standard"
					className={classes.dayType}
					value={value.dayType}
					onChange={handleDayTypeChange}
					SelectProps={{
						classes: {
							select: classes.select,
						},
					}}
				>
					{renderDayTypeItems()}
				</TextField>
			</Grid>
			<Grid item xs={12} md={9}>
				<Grid container spacing={3}>
					<Grid item xs={5} md={4}>
						<Typography variant="body2" color="secondary">
							{t('common:times.from', 'From')}
						</Typography>
					</Grid>
					<Grid item xs={5} md={4}>
						<Typography variant="body2" color="secondary">
							{t('common:times.to', 'To')}
						</Typography>
					</Grid>
				</Grid>
				{value.slots.map((slot, index, slots) => {
					const isDuplicate = isDeliveryTimeSlotDuplicate(slot, index, slots);
					return (
						<Box key={index}>
							<Grid container spacing={3} alignItems="center">
								<Grid item xs={5} md={4}>
									<TimePicker
										onChange={(startTime) => handleStartTimeChange(startTime, index)}
										selectedDate={moment(slot.startTime, 'HH:mm').toISOString()}
										noMinDate
										underlined
										hideLastTime
										error={isDuplicate}
									/>
								</Grid>
								<Grid item xs={5} md={4}>
									<TimePicker
										onChange={(endTime) => handleEndTimeChange(endTime, index)}
										selectedDate={moment(slot.endTime, 'HH:mm').toISOString()}
										startDate={moment(slot.startTime, 'HH:mm').toISOString()}
										hideDuration
										noMinDate
										underlined
										error={isDuplicate}
									/>
								</Grid>
								<Grid item xs={2} md={4}>
									<IconButton size="small" onClick={() => onDelete(index)}>
										<RiCloseLine />
									</IconButton>
								</Grid>
							</Grid>
							{isDuplicate && (
								<FormHelperText error>
									{t('common:validation.cannotBeDuplicate', {
										defaultValue: '{{field}} cannot be duplicate',
										field: t('delivery.timeSlot', 'Time slot'),
									})}
								</FormHelperText>
							)}
						</Box>
					);
				})}
				<Box mt={1}>
					<Button variant="text" color="primary" onClick={handleAddNewSlot}>{`+ ${t(
						'delivery.addSlot',
						'Add slot',
					)}`}</Button>
				</Box>
			</Grid>
		</Grid>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		select: {
			padding: theme.spacing(1.25),
			paddingRight: theme.spacing(4),
		},
		dayType: {
			[theme.breakpoints.up('md')]: {
				marginTop: 33,
			},
		},
	}),
);

export default SingleDeliveryTime;
